import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon, IconButton } from '@openedx/paragon';
import { FormattedMessage } from '@edx/frontend-platform/i18n';
import { Bookmark, BookmarkBorder } from '@openedx/paragon/icons';

import { removeBookmark, addBookmark } from '@src/courseware/course/bookmark/data/thunks';

const addBookmarkLabel = (
  <FormattedMessage
    id="unit.bookmark.button.add.bookmark"
    defaultMessage="Bookmark this page"
    description="The button to bookmark a page"
  />
);

const hasBookmarkLabel = (
  <FormattedMessage
    id="unit.bookmark.button.remove.bookmark"
    defaultMessage="Bookmarked"
    description="The button to show a page is bookmarked and the button to remove that bookmark"
  />
);

const BookmarkButton = ({
  dispatch,
  isBookmarked,
  isProcessing,
  unitId,
}) => {
  const bookmarkState = isBookmarked ? 'bookmarked' : 'default';
  const state = isProcessing ? `${bookmarkState}Processing` : bookmarkState;
  const iconStates = {
    default: BookmarkBorder,
    defaultProcessing: BookmarkBorder,
    bookmarked: Bookmark,
    bookmarkedProcessing: Bookmark,
  };
  const altState = {
    default: addBookmarkLabel,
    defaultProcessing: addBookmarkLabel,
    bookmarked: hasBookmarkLabel,
    bookmarkedProcessing: hasBookmarkLabel,
  };

  const toggleBookmark = useCallback(() => {
    if (isBookmarked) {
      dispatch(removeBookmark(unitId));
    } else {
      dispatch(addBookmark(unitId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBookmarked, unitId]);

  return (
    <IconButton
      src={iconStates[state]}
      iconAs={Icon}
      onClick={toggleBookmark}
      aria-busy={isProcessing}
      disabled={isProcessing}
      alt={altState[state]}
      className="icon-hover"
    />
  );
};

BookmarkButton.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  unitId: PropTypes.string.isRequired,
};

export default BookmarkButton;
